import { useCallback, useContext, useEffect, useLayoutEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { GlobalContext } from '../../../../contexts/GlobalContext';
import { RacingContext } from '../../../Home/RacingPage';
import Simple from './Simple';
import Advanced from './Advanced';
import Header from './Header';
import Result from './Result';
import Adjust from './Adjust';
import RaceSimulatorBottom from './RaceSimulatorBottom';
import './index.scss';
import { useWindowSize } from '../../../Common/CommonHooks';
import useFetch from '../../../Common/hooks/useFetch';
import { useLocalForage } from '../../../Common/hooks';
import { isLocalMeeting } from '../../../Common/home-common';
import { getRunnerData } from '../../Common/RCOddsTableBase';
import ErrorModal from './ErrorModal';
import { useNavigate } from 'react-router-dom';
import { isVoidedRace, isAbandonedRace } from '../../Common/Common';
import { Loading, Toast } from '../../../Common/ui-components';
import { useRaceSimUnityWebGLPopUp, shouldPlayRaceSim, useWatchRaceSimResult } from './UnityWebGLPopUp';
import Button from '../../../Common/ui-components/Button';
import { useAdobeLaunch } from '@/src/components/Common/utils/adobeLaunch';

export const levelOptions = [
    { label: 'A+', value: 'A+', inputValue: 100, min: 96, max: 100 },
    { label: 'A', value: 'A', inputValue: 93, min: 90, max: 95 },
    { label: 'A-', value: 'A-', inputValue: 86, min: 83, max: 89 },
    { label: 'B+', value: 'B+', inputValue: 79, min: 76, max: 82 },
    { label: 'B', value: 'B', inputValue: 72, min: 70, max: 75 },
    { label: 'B-', value: 'B-', inputValue: 65, min: 62, max: 69 },
    { label: 'C+', value: 'C+', inputValue: 58, min: 56, max: 61 },
    { label: 'C', value: 'C', inputValue: 51, min: 48, max: 55 },
    { label: 'C-', value: 'C-', inputValue: 44, min: 40, max: 47 },
    { label: 'D+', value: 'D+', inputValue: 37, min: 36, max: 39 },
    { label: 'D', value: 'D', inputValue: 30, min: 28, max: 35 },
    { label: 'D-', value: 'D-', inputValue: 23, min: 20, max: 27 },
    { label: 'E+', value: 'E+', inputValue: 16, min: 10, max: 19 },
    { label: 'E', value: 'E', inputValue: 0, min: 0, max: 9 }
];

export const levelMap = {
    'A+': '100',
    A: '093',
    'A-': '086',
    'B+': '079',
    B: '072',
    'B-': '065',
    'C+': '058',
    C: '051',
    'C-': '044',
    'D+': '037',
    D: '030',
    'D-': '023',
    'E+': '016',
    E: '000'
};

const columns = {
    alw: true,
    no: true,
    colour: true,
    horseName: true,
    draw: true,
    wt: true,
    jocky: true,
    trainer: true,
    bodyWT: false,
    rtg: false,
    gear: false,
    l6: false,
    pwin0: false,
    pwin1: false,
    win: true,
    place: true,
    wp: true
};

const inputFactorsOrder = ['jk', 'tn', 'fit', 'dw', 'td'];
export const inputImportanceOrder = ['jk', 'tn', 'fit', 'dw', 'td', 'hr', 'l6'];

export const transformHorseAttributes = (horseAttributesStr) => {
    if (!horseAttributesStr) return;

    return {
        td: [horseAttributesStr.slice(0, 3) * 10, horseAttributesStr.slice(3, 6) * 10],
        rs: horseAttributesStr.slice(6, 7),
        hr: horseAttributesStr.slice(7, 10),
        fit: levelOptions.find(
            (i) =>
                i.min <= parseInt(horseAttributesStr.slice(10, 13)) &&
                parseInt(horseAttributesStr.slice(10, 13)) <= i.max
        )?.value,
        jk: levelOptions.find(
            (i) =>
                i.min <= parseInt(horseAttributesStr.slice(13, 16)) &&
                parseInt(horseAttributesStr.slice(13, 16)) <= i.max
        )?.value,
        tn: levelOptions.find(
            (i) =>
                i.min <= parseInt(horseAttributesStr.slice(16, 19)) &&
                parseInt(horseAttributesStr.slice(16, 19)) <= i.max
        )?.value
    };
};

const RaceSimulatorInner = () => {
    const { t, i18n } = useTranslation();
    const { isMobileSize } = useWindowSize();
    const globalContext = useContext(GlobalContext);
    const context = useContext(RacingContext);

    const content = context.content;
    const handleVenueClick = context.handleVenueClick;
    const { venue, raceNo, date } = content;
    const mtg = context?.content?.meeting;
    const mid = mtg?.id;

    const {
        rsShowType,
        setRsShowType,
        rsShowPage,
        setRsShowPage,
        rsIsWatchVideo,
        rsRaceData,
        setRsRaceData,
        setViewResultButtomDisabled,
        resetRsImportanceValue,
        setLocalParaRef,
        disableRsMenu
    } = globalContext;

    const { open, prefetch } = useRaceSimUnityWebGLPopUp();
    useWatchRaceSimResult({
        onChange: async (result) => {
            if (result.reason === 'completed' && shouldPlayRaceSim(result.errorCode)) {
                const raceSimStorageValue = await loadLocalImportanceValue();
                console.debug({ result, mid, raceNo });
                // if user is viewing on the same race as the returned race sim result
                // set state from latest value in index DB
                // avoid relying on LocalImportanceValue because it is a state and steState is async without ways to await it
                if (mid === result.meetingId && raceNo === result.raceNo) {
                    const value = raceSimStorageValue.find((i) => i?.mid === mid);
                    if (!value) return;

                    const raceDataId = value.para.findIndex((i) => i?.no === parseInt(raceNo));
                    if (raceDataId === -1) return;

                    const { rsShowPage, crossingLineImageUrl, result, resultVideoUrl, rsShowType } =
                        value.para[raceDataId];
                    setRsShowPage(rsShowPage);
                    setCrossingLineImageUrl(crossingLineImageUrl);
                    setResult(result);
                    setResultVideoUrl(resultVideoUrl);
                    setRsResultShowMode(rsShowType);
                    setIsOpenAdjust(false);

                    return;
                }

                toastApi({
                    message: t('RS_COMPLETED', { raceNo: result.raceNo }),
                    showTime: window.RaceSimulatorConfig.rsCompletedToastShowTime,
                    button: (
                        <Button
                            style={{ fontSize: 13 }}
                            width="100px"
                            height="28px"
                            onClick={() => {
                                handleVenueClick(result.date, result.venue, result.raceNo);
                            }}
                        >
                            {t('LB_RS_VIEW_RESULT')}
                        </Button>
                    )
                });
            }
        }
    });

    useEffect(() => {
        const { abort } = prefetch();
        return () => abort();
    }, [prefetch]);

    const Glossary = window.RaceSimulatorConfig.Glossary[i18n.language];
    const KeyFactorsOrder = window.RaceSimulatorConfig.KeyFactorsOrder;
    const ImportanceOrder = window.RaceSimulatorConfig.ImportanceOrder;
    const classNameMap = window.RaceSimulatorConfig.classNameMap;
    const [rsKeyFactors, setRsKeyFactors] = useState([]);
    const [rsImportance, setRsImportance] = useState([]);

    const [isEditOpen, setIsEditOpen] = useState(false);
    const [isOpenAdjust, setIsOpenAdjust] = useState(false);
    const [showFactors, setShowFactors] = useState(false);
    const [rsResultShowMode, setRsResultShowMode] = useState('Simple');

    const {
        data: raceInfoData,
        error,
        loading
    } = useFetch(window.RaceSimulatorConfig.getParamsUrl(mtg?.date?.replace(/\-/g, '')));

    const getDefaultHorseAttributes = useCallback(
        (runnerNo) => {
            if (!raceInfoData) return;

            const horseAttributesStr = raceInfoData.raceinfo?.[`R${raceNo}`]?.p3?.split('/')?.[runnerNo];
            if (!horseAttributesStr || horseAttributesStr === '~') return;

            return transformHorseAttributes(horseAttributesStr);
        },
        [raceInfoData, raceNo]
    );

    let isLocal = isLocalMeeting(venue);
    const [runnersData, setRunnersData] = useState([]);

    const modifiedHorseAttributes = useMemo(() => {
        let hasModified = {
            td: false,
            rs: false,
            hr: false,
            fit: false,
            jk: false,
            tn: false
        };

        if (!raceInfoData) return hasModified;

        for (const runner of runnersData) {
            const runnerNo = Number(runner.no);
            const currentHorseAttributes = runner.simExtendInfo;
            const defaultHorseAttributes = getDefaultHorseAttributes(runnerNo - 1);

            if (!defaultHorseAttributes || !currentHorseAttributes) break;

            if (
                currentHorseAttributes.td?.[0] !== defaultHorseAttributes.td[0] ||
                currentHorseAttributes.td?.[1] !== defaultHorseAttributes.td[1]
            ) {
                hasModified.td = true;
            }
            if (Number(currentHorseAttributes.rs) !== Number(defaultHorseAttributes.rs)) hasModified.rs = true;
            if (Number(currentHorseAttributes.hr) !== Number(defaultHorseAttributes.hr)) hasModified.hr = true;
            if (currentHorseAttributes.fit !== defaultHorseAttributes.fit) hasModified.fit = true;
            if (currentHorseAttributes.jk !== defaultHorseAttributes.jk) hasModified.jk = true;
            if (currentHorseAttributes.tn !== defaultHorseAttributes.tn) hasModified.tn = true;
        }

        return hasModified;
    }, [getDefaultHorseAttributes, raceInfoData, runnersData]);

    const [result, setResult] = useState('');
    const [isOpenErrorModal, setIsOpenErrorModal] = useState(error);
    const [filterValue, setFilterValue] = useState(['All']);
    const [crossingLineImageUrl, setCrossingLineImageUrl] = useState('');

    const [resultVideoUrl, setResultVideoUrl] = useState('');
    const [showMore, setShowMore] = useState(false);
    const navigate = useNavigate();

    const [resultStatus, setResultStatus] = useState(''); // ABANDONED VOID FINISHED NOPOOLS

    const [toastApi, toastContent] = Toast.useToast({ max: 1, showTime: 3000, isRs: true });
    // artificially updating the URL to trigger WA page view event
    useEffect(() => {
        const skipAnimation = resultVideoUrl.charAt(1) === '1';
        const isWatchVideo = `VID:${skipAnimation ? 'N' : 'Y'}`;

        if (rsShowPage === 'RS_KEY_FACTORS') {
            // only allow to pass ?b_cid= when clicking Select Different Factor
            const searchParams = new URLSearchParams(window.location.search);
            for (const key of searchParams.keys()) {
                if (key !== 'b_cid') searchParams.delete(key);
            }
            searchParams.set('mode', rsShowType);
            navigate({ search: searchParams.toString(), hash: '' }, { replace: true });
            return;
        }

        if (rsShowPage === 'RS_RESULT') {
            const searchParams = new URLSearchParams(window.location.search);
            for (const [key, value] of searchParams.entries()) {
                // allow to pass ?b_cid= from entry point only, avoid passing the value set when clicking "Select Other Factors"
                if (key !== 'b_cid' || (key === 'b_cid' && !/^SPLDSP_.+_Nav$/.test(value))) {
                    searchParams.delete(key);
                }
            }
            searchParams.set('mode', rsResultShowMode);
            searchParams.set('userPreference', isWatchVideo);

            navigate({ search: searchParams.toString(), hash: 'result' }, { replace: true });
            return;
        }
    }, [navigate, resultVideoUrl, rsResultShowMode, rsShowPage, rsShowType]);

    useLayoutEffect(() => {
        if (!content) return;
        const { raceNo, meeting } = content;
        const { poolInvs, races } = meeting;
        const isNoBetService = poolInvs.filter((x) => x.leg.races[0] === raceNo).length === 0;
        const isVoided = isVoidedRace(content, meeting, raceNo);
        const isAbandon = isAbandonedRace(meeting, raceNo);
        const isFinished = ['CLOSED', 'RESULT'].includes(mtg.races.filter((x) => x.no === raceNo)[0]?.status);
        if (isAbandon) {
            setResultStatus('ABANDONED');
            setFilterValue(['All']);
        } else if (isVoided) {
            setResultStatus('VOID');
            setFilterValue(['All']);
        } else if (isNoBetService) {
            setResultStatus('NOPOOLS');
            setFilterValue(['All']);
        } else if (isFinished) {
            setResultStatus('');
        } else {
            setResultStatus('');
        }
    }, [content]);

    useEffect(() => {
        if (error || disableRsMenu) {
            setIsOpenErrorModal(true);
        }
    }, [error, disableRsMenu]);

    useEffect(() => {
        if (rsKeyFactors.length > 0 && rsImportance.length > 0 && runnersData.length > 0 && raceInfoData) {
            const lang = i18n.language === 'en' ? '2' : '1';
            const tv = rsIsWatchVideo ? '0' : '1';
            let factors = '00000';
            let importance = '';
            if (rsShowType === 'Advanced') {
                inputImportanceOrder.forEach((item) => {
                    const t = rsImportance.find((i) => {
                        return item === i?.key;
                    });
                    importance = `${importance}${String(t?.value ? t?.value * 10 : '000').padStart(3, '0')}`;
                });
            } else {
                inputFactorsOrder.forEach((item) => {
                    const t = rsKeyFactors.find((i) => {
                        return item === i?.key;
                    });
                    importance = `${importance}${t?.checked ? '100' : '000'}`;
                });
                importance = `${importance}100000`;
            }
            const digit29 = '1';

            const scrachedSign = runnersData
                .map((i) => {
                    return i?.isScratch ? '1' : '0';
                })
                .join('');

            const p3 = runnersData
                .map((item, index) => {
                    const i = rsShowType === 'Advanced' ? item.simExtendInfo : getDefaultHorseAttributes(index);
                    if (!i) return '';
                    return `${String(i.td[0] / 10).padStart(3, '0')}${String(i.td[1] / 10).padStart(3, '0')}${String(
                        i.rs
                    )}${String(i.hr).padStart(3, '0')}${String(levelMap[i.fit]).padStart(3, '0')}${String(
                        levelMap[i.jk]
                    ).padStart(3, '0')}${String(levelMap[i.tn]).padStart(3, '0')}`;
                })
                .join('/');
            // const p4 = raceInfoData?.raceinfo?.[`R${raceNo}`]?.[`p4`];
            const p4 = raceInfoData?.raceinfo?.[`R${raceNo}`]?.[`p4`];

            let racedata = `${lang}${tv}${factors}${importance}${digit29}~${scrachedSign}~${p3}/~${p4}`;
            setRsRaceData(racedata);
        }
    }, [
        getDefaultHorseAttributes,
        i18n.language,
        raceInfoData,
        raceNo,
        rsImportance,
        rsIsWatchVideo,
        rsKeyFactors,
        rsShowType,
        runnersData
    ]);

    const {
        value: LocalImportanceValue = [],
        setValue: setLocalImportanceValue,
        loaded: localImportanceValueLoaded,
        loadValue: loadLocalImportanceValue
    } = useLocalForage('LocalImportanceValue', [], (value) => {
        try {
            if (value) {
                let filterValueArray = [];
                if (Array.isArray(value)) {
                    filterValueArray = value.filter((item) => {
                        if (item?.updDt) {
                            return (
                                parseInt(item.updDt) + window.RaceSimulatorConfig.ImportanceValueExpiryDate >=
                                new Date().getTime()
                            );
                        }
                        return false;
                    });
                    return filterValueArray;
                }
            }
            return [];
        } catch (error) {}

        return [];
    });

    const restoreLocalPara = useCallback(
        (raceData) => {
            if (!raceData.rsShowPage) {
                raceData.rsShowPage = 'RS_KEY_FACTORS';
                setRsShowPage('RS_KEY_FACTORS');
            } else {
                setRsShowPage(raceData.rsShowPage);
            }

            if (!raceData.rsShowType) {
                raceData.rsShowType = rsShowType || 'Simple';
                setRsResultShowMode(rsShowType || 'Simple');
            } else {
                setRsResultShowMode(raceData.rsShowType);
            }

            if (!raceData.result) {
                raceData.result = '';
                setResult('');
            } else {
                setResult(raceData.result);
            }

            if (!raceData.filterValue) {
                raceData.filterValue = ['All'];
                setFilterValue(['All']);
            } else {
                setFilterValue(raceData.filterValue);
            }

            if (!raceData.crossingLineImageUrl) {
                raceData.crossingLineImageUrl = '';
                setCrossingLineImageUrl('');
            } else {
                setCrossingLineImageUrl(raceData.crossingLineImageUrl);
            }

            if (!raceData.resultVideoUrl) {
                raceData.resultVideoUrl = '';
                setResultVideoUrl('');
            } else {
                setResultVideoUrl(raceData.resultVideoUrl);
            }

            if (!raceData.showMore) {
                raceData.showMore = false;
                setShowMore(false);
            } else {
                setShowMore(raceData.showMore);
            }

            if (!raceData.showFactors) {
                raceData.showFactors = false;
                setShowFactors(false);
            } else {
                setShowFactors(raceData.showFactors);
            }
        },
        [rsShowType]
    );

    const reset = (isDefault = false) => {
        const DefaultImportanceValue = window.RaceSimulatorConfig.DefaultImportanceValue;
        if (isDefault) {
            setRsImportance(
                ImportanceOrder.map((i) => {
                    return {
                        key: i,
                        title: Glossary[i]['title'],
                        className: classNameMap[i],
                        value: DefaultImportanceValue
                    };
                })
            );
            return;
        }

        let localValue = LocalImportanceValue.find((i) => i?.mid === mid);
        if (!localValue) {
            localValue = { mid, para: [], updDt: new Date().getTime() };
            LocalImportanceValue.push(localValue);
        }
        let raceData = localValue.para.find((i) => i?.no === raceNo);
        if (!raceData) {
            raceData = {
                no: raceNo,
                jk: DefaultImportanceValue,
                tn: DefaultImportanceValue,
                fit: DefaultImportanceValue,
                dw: DefaultImportanceValue,
                td: DefaultImportanceValue,
                hr: DefaultImportanceValue,
                l6: DefaultImportanceValue,
                runners: []
            };
            localValue.para.push(raceData);
            localValue.updDt = new Date().getTime();
        }
        const rsKeyFactorsTmp = raceData?.rsKeyFactors || [];
        setRsKeyFactors(
            KeyFactorsOrder.map((i) => {
                return {
                    key: i,
                    title: Glossary[i]['title'],
                    checked: rsKeyFactorsTmp.includes(i),
                    className: classNameMap[i]
                };
            })
        );
        if (raceNo && mtg && isLocal && raceInfoData) {
            let race = mtg?.races?.find((data) => data.no == raceNo);
            let runners = race?.runners || [];
            const runnersRows = [];
            let r = 0;
            for (let i = 0; i < runners.length; i++) {
                if (runners[i].status.toUpperCase() === 'STANDBY') {
                    continue;
                }

                const defaultHorseAttributes = getDefaultHorseAttributes(r);
                if (!defaultHorseAttributes) break;

                let runner = getRunnerData(
                    columns,
                    context,
                    t,
                    isLocal,
                    mtg,
                    runners[i],
                    context.content.raceNo,
                    mtg.pmPools,
                    i18n.language
                );
                let simExtendInfo = raceData.runners.find((i) => i?.horseId === runner.horseId);

                if (!simExtendInfo?.td) {
                    simExtendInfo = {
                        horseId: runner.horseId,
                        ...defaultHorseAttributes,
                        updAtr: []
                    };
                    raceData.runners.push(simExtendInfo);
                }
                runner.simExtendInfo = simExtendInfo;
                runnersRows.push(runner);
                r++;
            }
            setRunnersData(runnersRows);
        }

        restoreLocalPara(raceData);

        setRsImportance(
            ImportanceOrder.map((i) => {
                return {
                    key: i,
                    title: Glossary[i]['title'],
                    className: classNameMap[i],
                    value: raceData[i] >= 0 && raceData[i] <= 10 ? raceData[i] : DefaultImportanceValue
                };
            })
        );
        setLocalImportanceValue([...LocalImportanceValue]);
    };

    useLayoutEffect(() => {
        if (mid && raceNo && isLocal && i18n.language && Glossary && raceInfoData && localImportanceValueLoaded) {
            reset();
        }
    }, [mid, raceNo, isLocal, i18n.language, Glossary, raceInfoData, localImportanceValueLoaded]);

    const reFreshOdds = () => {
        let localValue = LocalImportanceValue.find((i) => i?.mid === mid);
        if (!localValue) {
            return;
        }
        let raceData = localValue.para.find((i) => i?.no === raceNo);
        if (!raceData) {
            return;
        }
        if (raceNo && mtg && isLocal && raceInfoData) {
            let race = mtg?.races?.find((data) => data.no == raceNo);
            let runners = race?.runners || [];
            const p3 = raceInfoData?.raceinfo?.[`R${raceNo}`]?.[`p3`]?.split('/');
            const runnersRows = [];
            let r = 0;
            for (let i = 0; i < runners.length && p3; i++) {
                if (runners[i].status.toUpperCase() === 'STANDBY') {
                    continue;
                }
                const p3r = p3[r];
                if (!p3r || p3r === '~') {
                    break;
                }
                let runner = getRunnerData(
                    columns,
                    context,
                    t,
                    isLocal,
                    mtg,
                    runners[i],
                    context.content.raceNo,
                    mtg.pmPools,
                    i18n.language
                );
                let rData = runnersData.find((i) => i?.simExtendInfo?.horseId === runner.horseId);
                let LocalSimExtendInfo = raceData.runners.find((i) => i?.horseId === runner.horseId);
                runner.simExtendInfo = rData?.simExtendInfo ? rData?.simExtendInfo : LocalSimExtendInfo;
                if (!runner.simExtendInfo) break;
                runnersRows.push(runner);
                r++;
            }
            if (runnersRows.length > 0) setRunnersData(runnersRows);
        }
    };
    useLayoutEffect(() => {
        if (raceNo && mtg?.pmPools?.length > 0 && raceInfoData) {
            reFreshOdds();
        }
    }, [raceNo, context.content, raceInfoData]);

    const resetRunners = useCallback(
        (only) => {
            const DefaultImportanceValue = window.RaceSimulatorConfig.DefaultImportanceValue;
            let localValue = LocalImportanceValue.find((i) => i?.mid === mid);
            if (!localValue) {
                localValue = { mid, para: [], updDt: new Date().getTime() };
                LocalImportanceValue.push(localValue);
            }
            let raceDataId = localValue.para.findIndex((i) => i?.no === raceNo);
            let raceData;
            if (!(raceDataId >= 0)) {
                raceDataId = localValue.para.length || 0;
                raceData = localValue.para[raceDataId] = {
                    no: raceNo,
                    rsShowPage: localValue.para[raceDataId]?.rsShowPage || 'RS_KEY_FACTORS',
                    result: localValue.para[raceDataId]?.result || '',
                    filterValue: localValue.para[raceDataId]?.filterValue || ['All'],
                    crossingLineImageUrl: localValue.para[raceDataId]?.crossingLineImageUrl || '',
                    resultVideoUrl: localValue.para[raceDataId]?.resultVideoUrl || '',
                    showMore: localValue.para[raceDataId]?.showMore || false,
                    jk:
                        localValue.para[raceDataId]?.jk !== undefined
                            ? localValue.para[raceDataId]?.jk
                            : DefaultImportanceValue,
                    tn:
                        localValue.para[raceDataId]?.tn !== undefined
                            ? localValue.para[raceDataId]?.tn
                            : DefaultImportanceValue,
                    fit:
                        localValue.para[raceDataId]?.fit !== undefined
                            ? localValue.para[raceDataId]?.fit
                            : DefaultImportanceValue,
                    dw:
                        localValue.para[raceDataId]?.dw !== undefined
                            ? localValue.para[raceDataId]?.dw
                            : DefaultImportanceValue,
                    td:
                        localValue.para[raceDataId]?.td !== undefined
                            ? localValue.para[raceDataId]?.td
                            : DefaultImportanceValue,
                    hr:
                        localValue.para[raceDataId]?.hr !== undefined
                            ? localValue.para[raceDataId]?.hr
                            : DefaultImportanceValue,
                    l6:
                        localValue.para[raceDataId]?.l6 !== undefined
                            ? localValue.para[raceDataId]?.l6
                            : DefaultImportanceValue,
                    runners: [],
                    rsKeyFactors: rsKeyFactors.filter((i) => i.checked).map((j) => j.key)
                };
            } else {
                raceData = localValue.para[raceDataId] = {
                    ...localValue.para[raceDataId],
                    runners: [],
                    rsKeyFactors: rsKeyFactors.filter((i) => i.checked).map((j) => j.key)
                };
            }

            if (raceNo && mtg && isLocal && raceInfoData) {
                let race = mtg?.races?.find((data) => data.no == raceNo);
                let runners = race?.runners || [];
                const runnersRows = [];
                let r = 0;
                for (let i = 0; i < runners.length; i++) {
                    if (runners[i].status.toUpperCase() === 'STANDBY') {
                        continue;
                    }

                    const defaultHorseAttributes = getDefaultHorseAttributes(r);
                    if (!defaultHorseAttributes) break;

                    let runner = getRunnerData(
                        columns,
                        context,
                        t,
                        isLocal,
                        mtg,
                        runners[i],
                        context.content.raceNo,
                        mtg.pmPools,
                        i18n.language
                    );
                    let simExtendInfo = {
                        horseId: runner.horseId,
                        ...defaultHorseAttributes,
                        updAtr: []
                    };
                    raceData.runners.push(simExtendInfo);
                    runner.simExtendInfo = simExtendInfo;
                    runnersRows.push(runner);
                    r++;
                }
                setRunnersData(runnersRows);
            }
            localValue.updDt = new Date().getTime();
            setLocalImportanceValue([...LocalImportanceValue]);
            if (only) {
                toastApi({ message: t('RS_HORSE_ATTRIBUTE_PAGE_RESET'), alignCenter: true });
            }
        },
        [
            mid,
            raceNo,
            mtg.races,
            isLocal,
            setLocalImportanceValue,
            setRunnersData,
            LocalImportanceValue,
            i18n.language,
            Glossary,
            mtg?.pmPools?.length,
            rsKeyFactors,
            toastApi
        ]
    );

    resetRsImportanceValue.current = useCallback(() => {
        const DefaultImportanceValue = window.RaceSimulatorConfig.DefaultImportanceValue;
        let localValue = LocalImportanceValue.find((i) => i?.mid === mid);
        if (!localValue) {
            localValue = { mid, para: [], updDt: new Date().getTime() };
            LocalImportanceValue.push(localValue);
        }
        let raceDataId = localValue.para.findIndex((i) => i?.no === raceNo);
        if (!(raceDataId >= 0)) {
            raceDataId = localValue.para.length || 0;
        }
        localValue.para[raceDataId] = {
            no: raceNo,
            rsShowPage: localValue.para[raceDataId]?.rsShowPage || 'RS_KEY_FACTORS',
            result: localValue.para[raceDataId]?.result || '',
            filterValue: localValue.para[raceDataId]?.filterValue || ['All'],
            crossingLineImageUrl: localValue.para[raceDataId]?.crossingLineImageUrl || '',
            resultVideoUrl: localValue.para[raceDataId]?.resultVideoUrl || '',
            showMore: localValue.para[raceDataId]?.showMore || false,
            jk: DefaultImportanceValue,
            tn: DefaultImportanceValue,
            fit: DefaultImportanceValue,
            dw: DefaultImportanceValue,
            td: DefaultImportanceValue,
            hr: DefaultImportanceValue,
            l6: DefaultImportanceValue,
            runners: localValue.para[raceDataId]?.runners || [],
            rsKeyFactors: rsKeyFactors.filter((i) => i.checked).map((j) => j.key)
        };
        localValue.updDt = new Date().getTime();
        setRsImportance(
            ImportanceOrder.map((i) => {
                return {
                    key: i,
                    title: Glossary[i]['title'],
                    className: classNameMap[i],
                    value: DefaultImportanceValue
                };
            })
        );
        setLocalImportanceValue([...LocalImportanceValue]);
        resetRunners();
        toastApi({ message: t('RS_HORSE_SELECTOR_PAGE_RESET') });
    }, [
        mid,
        raceNo,
        LocalImportanceValue,
        setRsImportance,
        setLocalImportanceValue,
        Glossary,
        resetRunners,
        rsKeyFactors,
        toastApi
    ]);

    setLocalParaRef.current = useCallback(
        ({
            rsShowPage: rsShowPagePara,
            crossingLineImageUrl,
            simulationResult,
            filterValue,
            resultVideoUrl,
            showMore,
            showFactors,
            rsShowType: rsShowTypePara
        }) => {
            const DefaultImportanceValue = window.RaceSimulatorConfig.DefaultImportanceValue;
            let localValue = LocalImportanceValue.find((i) => i?.mid === mid);
            if (!localValue) {
                localValue = { mid, para: [], updDt: new Date().getTime() };
                LocalImportanceValue.push(localValue);
            }
            let raceDataId = localValue.para.findIndex((i) => i?.no === raceNo);
            if (!(raceDataId >= 0)) {
                raceDataId = localValue.para.length || 0;
            }
            localValue.para[raceDataId] = {
                no: raceNo,
                rsShowPage:
                    rsShowPagePara !== undefined
                        ? rsShowPagePara
                        : localValue.para[raceDataId]?.rsShowPage || 'RS_KEY_FACTORS',
                rsShowType:
                    rsShowTypePara !== undefined
                        ? rsShowTypePara
                        : localValue.para[raceDataId].rsShowType && rsShowPage === 'RS_RESULT'
                          ? localValue.para[raceDataId].rsShowType
                          : rsShowType || 'Simple',
                crossingLineImageUrl: (crossingLineImageUrl ?? localValue.para[raceDataId]?.crossingLineImageUrl) || '',
                result: (simulationResult ?? localValue.para[raceDataId]?.result) || '',
                filterValue: filterValue || localValue.para[raceDataId]?.filterValue || ['All'],
                resultVideoUrl: resultVideoUrl || localValue.para[raceDataId]?.resultVideoUrl || '',
                showMore: showMore !== undefined ? showMore : localValue.para[raceDataId]?.showMore || false,
                showFactors:
                    showFactors !== undefined ? showFactors : localValue.para[raceDataId]?.showFactors || false,
                jk:
                    localValue.para[raceDataId]?.jk !== undefined
                        ? localValue.para[raceDataId]?.jk
                        : DefaultImportanceValue,
                tn:
                    localValue.para[raceDataId]?.tn !== undefined
                        ? localValue.para[raceDataId]?.tn
                        : DefaultImportanceValue,
                fit:
                    localValue.para[raceDataId]?.fit !== undefined
                        ? localValue.para[raceDataId]?.fit
                        : DefaultImportanceValue,
                dw:
                    localValue.para[raceDataId]?.dw !== undefined
                        ? localValue.para[raceDataId]?.dw
                        : DefaultImportanceValue,
                td:
                    localValue.para[raceDataId]?.td !== undefined
                        ? localValue.para[raceDataId]?.td
                        : DefaultImportanceValue,
                hr:
                    localValue.para[raceDataId]?.hr !== undefined
                        ? localValue.para[raceDataId]?.hr
                        : DefaultImportanceValue,
                l6:
                    localValue.para[raceDataId]?.l6 !== undefined
                        ? localValue.para[raceDataId]?.l6
                        : DefaultImportanceValue,
                runners: localValue.para[raceDataId]?.runners || [],
                rsKeyFactors: rsKeyFactors.filter((i) => i.checked).map((j) => j.key)
            };
            setFilterValue(localValue.para[raceDataId].filterValue);
            setResult(localValue.para[raceDataId].result);
            setCrossingLineImageUrl(localValue.para[raceDataId].crossingLineImageUrl);
            setRsShowPage(localValue.para[raceDataId].rsShowPage);
            setRsShowType(rsShowTypePara !== undefined ? rsShowTypePara : rsShowType || 'Simple');
            // setRsResultShowMode(localValue.para[raceDataId].rsShowType);
            // setResultVideoUrl(localValue.para[raceDataId].resultVideoUrl);
            setShowMore(localValue.para[raceDataId].showMore);
            setShowFactors(localValue.para[raceDataId].showFactors);
            setLocalImportanceValue([...LocalImportanceValue]);
        },
        [LocalImportanceValue, setLocalImportanceValue, raceNo, mid, rsKeyFactors, rsShowType, rsShowPage]
    );

    const setHorseData = useCallback(
        (line, key, value) => {
            if (!rsImportance.some((i) => i.value > 0)) return false;

            let localValue = LocalImportanceValue.find((i) => i?.mid === mid);
            if (!localValue) {
                localValue = { mid, para: [], updDt: new Date().getTime() };
                LocalImportanceValue.push(localValue);
            }
            let raceDataId = localValue.para.findIndex((i) => i?.no === raceNo);
            if (!(raceDataId >= 0)) {
                raceDataId = localValue.para.length || 0;
            }
            runnersData[line].simExtendInfo = {
                ...runnersData[line].simExtendInfo,
                [key]: value,
                updAtr: [...new Set([...(runnersData[line].simExtendInfo?.updAtr || []), key])]
            };
            setRunnersData([...runnersData]);
        },
        [mid, raceNo, rsImportance, runnersData, setRunnersData, LocalImportanceValue]
    );

    const confirmHorseData = useCallback(
        (line, key) => {
            if (!rsImportance.some((i) => i.value > 0)) return false;

            let localValue = LocalImportanceValue.find((i) => i?.mid === mid);
            if (!localValue) {
                localValue = { mid, para: [], updDt: new Date().getTime() };
                LocalImportanceValue.push(localValue);
            }
            let raceDataId = localValue.para.findIndex((i) => i?.no === raceNo);
            if (!(raceDataId >= 0)) {
                raceDataId = localValue.para.length || 0;
            }
            localValue.para[raceDataId].runners[line] = runnersData[line].simExtendInfo;
            localValue.updDt = new Date().getTime();
            setLocalImportanceValue([...LocalImportanceValue]);
            return true;
        },
        [mid, raceNo, rsImportance, runnersData, setRunnersData]
    );

    const confirmImportanceValue = useCallback(() => {
        if (!rsImportance.some((i) => i.value > 0)) return false;

        let localValue = LocalImportanceValue.find((i) => i?.mid === mid);
        if (!localValue) {
            localValue = { mid, para: [], updDt: new Date().getTime() };
            LocalImportanceValue.push(localValue);
        }
        let raceDataId = localValue.para.findIndex((i) => i?.no === raceNo);
        if (!(raceDataId >= 0)) {
            raceDataId = localValue.para.length || 0;
        }
        ImportanceOrder.forEach((item, index) => {
            localValue.para[raceDataId][item] = rsImportance[index].value;
        });
        localValue.updDt = new Date().getTime();
        setLocalImportanceValue([...LocalImportanceValue]);
        return true;
    }, [mid, raceNo, rsImportance]);

    const setRsKeyFactorsFn = useCallback(
        (e, item, i) => {
            e.stopPropagation();
            const newItem = { ...item };
            newItem.checked = !item.checked;
            const newRsKeyFactors = [...rsKeyFactors];
            newRsKeyFactors[i] = newItem;
            setRsKeyFactors(newRsKeyFactors);
            let localValue = LocalImportanceValue.find((i) => i?.mid === mid);
            if (!localValue) {
                localValue = { mid, para: [], updDt: new Date().getTime() };
                LocalImportanceValue.push(localValue);
            }
            let raceDataId = localValue.para.findIndex((i) => i?.no === raceNo);
            if (!(raceDataId >= 0)) {
                raceDataId = localValue.para.length || 0;
            }
            localValue.para[raceDataId]['rsKeyFactors'] = newRsKeyFactors.filter((i) => i.checked).map((j) => j.key);
            localValue.updDt = new Date().getTime();
            setLocalImportanceValue([...LocalImportanceValue]);
            return true;
        },
        [mid, raceNo, rsKeyFactors]
    );

    useEffect(() => {
        if (rsShowType === 'Advanced') {
            setViewResultButtomDisabled(!rsImportance.some((i) => i.value > 0));
        } else {
            setViewResultButtomDisabled(!rsKeyFactors.some((i) => i.checked));
        }
    }, [rsKeyFactors, rsImportance, rsShowType]);

    const { sendAdobeEvent } = useAdobeLaunch();

    if (!localImportanceValueLoaded) {
        return <Loading />;
    }

    const handleViewResult = () => {
        const skipAnimation = rsRaceData.charAt(1) === '1';
        const isWatchVideo = `VID:${skipAnimation ? 'N' : 'Y'}`;
        const selectedKeyFactors = rsKeyFactors
            .map(({ key, checked }) => `${key.toUpperCase()}:${checked ? 'Y' : 'N'}`)
            .join(',');
        const importance = rsImportance.map(({ key, value }) => `${key.toUpperCase()}:${value}`).join(',');
        const isHorseAttributesModified = Object.entries(modifiedHorseAttributes)
            .map(([key, value]) => `HA-${key.toUpperCase()}:${value ? 'Y' : 'N'}`)
            .join(',');

        sendAdobeEvent({
            event: 'interaction',
            interaction: { eventID: 'eWinRacing_HorseSelector-Input_ClickViewResult' },
            content: { category: rsShowType, race_no: `${date?.replaceAll('-', '')}-${venue}-${raceNo}` },
            user: {
                preference:
                    rsShowType === 'Simple'
                        ? [isWatchVideo, selectedKeyFactors].join(',')
                        : [isWatchVideo, importance, isHorseAttributesModified].join(',')
            }
        });

        open();
    };

    return (
        <>
            <div
                className={`race-simulator-content ${
                    rsShowPage === 'RS_RESULT' ? 'race-simulator-content-result' : ''
                }`}
            >
                {/* {isUsedTour === false && (
                    <Tour isUsedTour={isUsedTour} setIsUsedTour={setIsUsedTour} setRsShowType={setRsShowType} />
                )} */}
                {toastContent}
                {resultStatus && (
                    <div className={`rs-result-message ${rsShowPage === 'RS_RESULT' ? 'page_result' : ''}`}>
                        {t(`RS_RACE_IS_${resultStatus}`)}
                    </div>
                )}
                {rsShowPage === 'RS_RESULT' ? (
                    <Result
                        {...{
                            result,
                            resultVideoUrl,
                            isMobileSize,
                            runnersData,
                            isLocal,
                            raceNo,
                            filterValue,
                            setFilterValue,
                            crossingLineImageUrl,
                            showMore,
                            setShowMore,
                            resultStatus,
                            showFactors,
                            setShowFactors,
                            getDefaultHorseAttributes,
                            rsKeyFactors,
                            rsImportance,
                            rsResultShowMode
                        }}
                    />
                ) : (
                    <>
                        <Header
                            isEditOpen={isEditOpen}
                            setIsEditOpen={setIsEditOpen}
                            reset={reset}
                            confirmImportanceValue={confirmImportanceValue}
                            rsShowType={rsShowType}
                            rsImportance={rsImportance}
                            setRsImportance={setRsImportance}
                            toastApi={toastApi}
                        />
                        {rsShowType === 'Advanced' ? (
                            <div className="adaptiveGrid-con">
                                <Advanced
                                    isEditOpen={isEditOpen}
                                    setIsEditOpen={setIsEditOpen}
                                    rsShowType={rsShowType}
                                    rsImportance={rsImportance}
                                    setRsImportance={setRsImportance}
                                    globalContext={globalContext}
                                    setHorseData={setHorseData}
                                />
                                {
                                    <Adjust
                                        {...{
                                            setHorseData,
                                            confirmHorseData,
                                            isOpenAdjust,
                                            setIsOpenAdjust,
                                            runnersData,
                                            setRunnersData,
                                            columns,
                                            resetRunners,
                                            reset,
                                            onViewResult: handleViewResult
                                        }}
                                        content={context.content}
                                    />
                                }
                            </div>
                        ) : (
                            <Simple
                                rsKeyFactors={rsKeyFactors}
                                setRsKeyFactorsFn={setRsKeyFactorsFn}
                                rsShowType={rsShowType}
                                globalContext={globalContext}
                            />
                        )}
                        {isMobileSize && rsShowPage !== 'RS_RESULT' && (
                            <div className="RS_REFERENCE" style={{ padding: '0 12px 12px' }}>
                                <div>{t('TNC_RG_MESSAGE1')}</div>
                                <div>{t('TNC_RG_MESSAGE2')}</div>
                                <div>{t('TNC_RG_MESSAGE3')}</div>
                            </div>
                        )}
                        {!isMobileSize && <RaceSimulatorBottom onViewResult={handleViewResult} />}
                    </>
                )}
            </div>
            {(error || disableRsMenu) && (
                <ErrorModal
                    onClose={() => {
                        navigate(`/${i18n.language}/racing/home/`);
                    }}
                    title={t('RS_JSON_DATA_ERROR_TITLE')}
                    content={t('RS_JSON_DATA_ERROR_CONTENT')}
                    isOpenModal={isOpenErrorModal}
                    setIsOpenModal={setIsOpenErrorModal}
                />
            )}
            {isMobileSize == false && rsShowPage !== 'RS_RESULT' && (
                <div className="RS_REFERENCE" style={{ marginTop: 16 }}>
                    <div>{t('TNC_RG_MESSAGE1')}</div>
                    <div>{t('TNC_RG_MESSAGE2')}</div>
                    <div>{t('TNC_RG_MESSAGE3')}</div>
                </div>
            )}
            {isMobileSize &&
                document.querySelector('.bottom-menu > header') &&
                createPortal(
                    <RaceSimulatorBottom onViewResult={handleViewResult} />,
                    document.querySelector('.bottom-menu > header')
                )}
        </>
    );
};
const RaceSimulator = () => {
    const context = useContext(RacingContext);
    const mtg = context?.content?.meeting;
    const mid = mtg?.id;
    return <RaceSimulatorInner key={mid} />;
};
export default RaceSimulator;
